import { useState } from 'react';
import { PortalData, SectionId } from '../../types/portal';
import { 
  Newspaper, 
  FileText, 
  Phone, 
  Warning, 
  Calendar, 
  Images,
  EnvelopeSimple,
  MapPin,
  Clock,
  Download,
  CaretRight,
  ArrowRight,
  CalendarBlank
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import Lightbox from '../Lightbox';

interface ModernTemplateProps {
  data: PortalData;
}

// Section components
const NewsSection = ({ news, colors }: { news: PortalData['news']; colors: PortalData['settings']['colors'] }) => {
  const [selectedNews, setSelectedNews] = useState<PortalData['news'][0] | null>(null);
  
  if (!news || news.length === 0) return null;
  
  return (
    <section className="py-12 md:py-16" id="news">
      <div className="max-w-6xl mx-auto px-4">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-8"
          style={{ color: colors.text }}
        >
          Nyheter
        </h2>
        <div className={`grid gap-6 ${news.length === 1 ? 'grid-cols-1' : news.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {news.slice(0, 6).map((item) => (
            <article 
              key={item.id}
              className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group ${news.length === 1 ? 'md:flex' : ''}`}
            >
              {item.image_url && (
                <div className={`overflow-hidden ${news.length === 1 ? 'md:w-1/3 aspect-video md:aspect-auto' : 'aspect-video'}`}>
                  <img 
                    loading="lazy"
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className={`p-5 ${news.length === 1 ? 'md:flex-1 md:p-8' : ''}`}>
                <time className="text-sm opacity-60" style={{ color: colors.text }}>
                  {new Date(item.published_at).toLocaleDateString('sv-SE')}
                </time>
                <h3 
                  className={`font-semibold mt-2 ${news.length === 1 ? 'text-xl md:text-2xl' : 'text-lg line-clamp-2'}`}
                  style={{ color: colors.text }}
                >
                  {item.title}
                </h3>
                <p 
                  className={`mt-2 opacity-70 ${news.length === 1 ? 'text-base' : 'text-sm line-clamp-3'}`}
                  style={{ color: colors.text }}
                >
                  {item.content.replace(/<[^>]*>/g, '').substring(0, news.length === 1 ? 300 : 150)}...
                </p>
                <button 
                  onClick={() => setSelectedNews(item)}
                  className="mt-4 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all cursor-pointer"
                  style={{ color: colors.primary }}
                >
                  Läs mer <ArrowRight size={14} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* News Modal */}
      {selectedNews && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedNews(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedNews.image_url && (
              <div className="aspect-video overflow-hidden rounded-t-xl">
                <img 
                  loading="lazy"
                  src={selectedNews.image_url} 
                  alt={selectedNews.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-6">
              <time 
                className="text-xs uppercase tracking-wider opacity-60"
                style={{ color: colors.text }}
              >
                {new Date(selectedNews.published_at).toLocaleDateString('sv-SE')}
              </time>
              <h2 
                className="text-2xl font-bold mt-2 mb-4"
                style={{ color: colors.text }}
              >
                {selectedNews.title}
              </h2>
              <div 
                className="prose prose-slate max-w-none"
                style={{ color: colors.text }}
                dangerouslySetInnerHTML={{ __html: selectedNews.content }}
              />
              <button
                onClick={() => setSelectedNews(null)}
                className="mt-6 px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const DocumentsSection = ({ documents, colors }: { documents: PortalData['documents']; colors: PortalData['settings']['colors'] }) => {
  if (!documents || documents.length === 0) return null;
  
  // Group by category
  const categories = documents.reduce((acc, doc) => {
    const cat = doc.category || 'Övrigt';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<string, typeof documents>);
  
  return (
    <section className="py-12 md:py-16" style={{ backgroundColor: colors.background }} id="documents">
      <div className="max-w-6xl mx-auto px-4">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-8"
          style={{ color: colors.text }}
        >
          Dokument
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(categories).map(([category, docs]) => (
            <div key={category} className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 
                className="font-semibold text-lg mb-4 flex items-center gap-2"
                style={{ color: colors.text }}
              >
                <FileText size={20} style={{ color: colors.primary }} />
                {category}
              </h3>
              <ul className="space-y-3">
                {docs.map((doc) => (
                  <li key={doc.id}>
                    <a 
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                    >
                      <span 
                        className="text-sm font-medium"
                        style={{ color: colors.text }}
                      >
                        {doc.name}
                      </span>
                      <Download 
                        size={18} 
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: colors.primary }}
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ contact, organization, colors }: { 
  contact: PortalData['contact']; 
  organization: PortalData['organization'];
  colors: PortalData['settings']['colors'];
}) => {
  return (
    <section className="py-12 md:py-16" id="contact">
      <div className="max-w-6xl mx-auto px-4">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-8"
          style={{ color: colors.text }}
        >
          Kontakt
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact info */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 
              className="font-semibold text-lg mb-4"
              style={{ color: colors.text }}
            >
              Kontaktuppgifter
            </h3>
            <div className="space-y-4">
              {contact.board_email && (
                <a 
                  href={`mailto:${contact.board_email}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <EnvelopeSimple size={20} style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">E-post</p>
                    <p className="font-medium" style={{ color: colors.text }}>{contact.board_email}</p>
                  </div>
                </a>
              )}
              {contact.board_phone && (
                <a 
                  href={`tel:${contact.board_phone}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <Phone size={20} style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Telefon</p>
                    <p className="font-medium" style={{ color: colors.text }}>{contact.board_phone}</p>
                  </div>
                </a>
              )}
              {contact.address && (
                <div className="flex items-center gap-3 p-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <MapPin size={20} style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Adress</p>
                    <p className="font-medium" style={{ color: colors.text }}>{contact.address}</p>
                  </div>
                </div>
              )}
              {contact.office_hours && (
                <div className="flex items-center gap-3 p-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <Clock size={20} style={{ color: colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Öppettider</p>
                    <p className="font-medium" style={{ color: colors.text }}>{contact.office_hours}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Board members */}
          {contact.board_members && contact.board_members.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 
                className="font-semibold text-lg mb-4"
                style={{ color: colors.text }}
              >
                Styrelse
              </h3>
              <div className="space-y-3">
                {contact.board_members.map((member, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {member.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: colors.text }}>{member.name}</p>
                      <p className="text-sm text-slate-500">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const IssueReportSection = ({ organization, colors }: { 
  organization: PortalData['organization'];
  colors: PortalData['settings']['colors'];
}) => {
  return (
    <section 
      className="py-12 md:py-16"
      style={{ backgroundColor: `${colors.primary}10` }}
      id="issues"
    >
      <div className="max-w-6xl mx-auto px-4 text-center">
        <Warning size={48} style={{ color: colors.primary }} className="mx-auto mb-4" />
        <h2 
          className="text-2xl md:text-3xl font-bold mb-4"
          style={{ color: colors.text }}
        >
          Felanmälan
        </h2>
        <p 
          className="max-w-xl mx-auto mb-8 opacity-80"
          style={{ color: colors.text }}
        >
          Har du upptäckt ett fel i fastigheten? Rapportera det enkelt via vårt formulär.
        </p>
        <a 
          href={`/report?org=${organization.slug}`}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity"
          style={{ backgroundColor: colors.primary }}
        >
          Anmäl fel
          <ArrowRight size={18} />
        </a>
      </div>
    </section>
  );
};

const CalendarSection = ({ calendar, colors }: { calendar: PortalData['calendar']; colors: PortalData['settings']['colors'] }) => {
  const upcomingEvents = (calendar || [])
    .filter(event => new Date(event.start_date) >= new Date())
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
    .slice(0, 5);
  
  return (
    <section className="py-12 md:py-16" id="calendar">
      <div className="max-w-6xl mx-auto px-4">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-8"
          style={{ color: colors.text }}
        >
          Kalender
        </h2>
        
        {upcomingEvents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <CalendarBlank size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Inga kommande händelser.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {upcomingEvents.map((event, idx) => {
              const date = new Date(event.start_date);
              return (
                <div 
                  key={event.id}
                  className={cn(
                    "flex items-center gap-4 p-4",
                    idx !== upcomingEvents.length - 1 && "border-b border-slate-100"
                  )}
                  data-testid={`calendar-event-${idx}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex flex-col items-center justify-center text-white"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <span className="text-xs font-medium opacity-80">
                      {date.toLocaleDateString('sv-SE', { month: 'short' }).toUpperCase()}
                    </span>
                    <span className="text-xl font-bold">
                      {date.getDate()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: colors.text }}>{event.title}</h3>
                    {event.description && (
                      <p className="text-sm text-slate-500 line-clamp-1">{event.description}</p>
                    )}
                    {event.location && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                        <MapPin size={12} /> {event.location}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-slate-500">
                    {date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const GallerySection = ({ gallery, colors }: { gallery: PortalData['gallery']; colors: PortalData['settings']['colors'] }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!gallery || gallery.length === 0) {
    return (
      <section 
        className="py-12 md:py-16"
        style={{ backgroundColor: colors.background }}
        id="gallery"
      >
        <div className="max-w-6xl mx-auto px-4">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-8"
            style={{ color: colors.text }}
          >
            Bildgalleri
          </h2>
          <div className="text-center py-12">
            <Images size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Inga bilder i galleriet ännu.</p>
          </div>
        </div>
      </section>
    );
  }

  const openLightbox = (index: number) => {
    setSelectedIndex(index);
    setLightboxOpen(true);
  };
  
  return (
    <>
      <section 
        className="py-12 md:py-16"
        style={{ backgroundColor: colors.background }}
        id="gallery"
      >
        <div className="max-w-6xl mx-auto px-4">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-8"
            style={{ color: colors.text }}
          >
            Bildgalleri
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((image, index) => (
              <button 
                key={image.id}
                onClick={() => openLightbox(index)}
                className="aspect-square rounded-xl overflow-hidden bg-slate-100 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  loading="lazy"
                  src={image.thumbnail_url || image.url}
                  alt={image.caption || `Bild ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        images={gallery.map(img => ({ url: img.url, caption: img.caption }))}
        initialIndex={selectedIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

// Section renderer map
const SECTION_COMPONENTS: Record<SectionId, React.FC<{ data: PortalData }>> = {
  news: ({ data }) => <NewsSection news={data.news} colors={data.settings.colors} />,
  documents: ({ data }) => <DocumentsSection documents={data.documents} colors={data.settings.colors} />,
  contact: ({ data }) => <ContactSection contact={data.contact} organization={data.organization} colors={data.settings.colors} />,
  issues: ({ data }) => <IssueReportSection organization={data.organization} colors={data.settings.colors} />,
  calendar: ({ data }) => <CalendarSection calendar={data.calendar} colors={data.settings.colors} />,
  gallery: ({ data }) => <GallerySection gallery={data.gallery} colors={data.settings.colors} />
};

export default function ModernTemplate({ data }: ModernTemplateProps) {
  const { settings, organization, news, documents, calendar, gallery, contact } = data;
  const { colors, visible_sections } = settings;
  
  // Define preferred section order
  const sectionOrder: SectionId[] = ['news', 'contact', 'calendar', 'issues', 'documents', 'gallery'];
  
  // Filter visible_sections to only include sections with content
  const sectionsWithContent = visible_sections.filter(sectionId => {
    switch (sectionId) {
      case 'news':
        return news && news.length > 0;
      case 'documents':
        return documents && documents.length > 0;
      case 'calendar':
        return calendar && calendar.length > 0;
      case 'gallery':
        return gallery && gallery.length > 0;
      case 'contact':
        // Show contact if email or phone is available
        return contact?.board_email || contact?.board_phone || contact?.address;
      case 'issues':
        // Issues section (felanmälan) is always available if enabled
        return true;
      default:
        return true;
    }
  }).sort((a, b) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b));
  
  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: colors.background || '#f8fafc' }}
    >
      {/* Hero */}
      <header 
        className="relative h-64 md:h-80 flex items-end"
        style={{ 
          background: settings.hero_image_url 
            ? `url(${settings.hero_image_url}) center/cover` 
            : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 pb-8 w-full">
          <div className="flex items-end gap-4">
            {settings.logo_url && (
              <img 
                src={settings.logo_url}
                alt={organization.name}
                className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white p-2 object-contain shadow-lg"
              />
            )}
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white drop-shadow-lg">
                {organization.name}
              </h1>
              {settings.meta_description && (
                <p className="text-white/80 mt-1 text-sm md:text-base">
                  {settings.meta_description}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation pills */}
      <nav className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {sectionsWithContent.map(sectionId => {
              const config = { 
                news: { name: 'Nyheter', icon: Newspaper },
                documents: { name: 'Dokument', icon: FileText },
                contact: { name: 'Kontakt', icon: Phone },
                issues: { name: 'Felanmälan', icon: Warning },
                calendar: { name: 'Kalender', icon: Calendar },
                gallery: { name: 'Galleri', icon: Images }
              }[sectionId];
              
              if (!config) return null;
              const Icon = config.icon;
              
              return (
                <a
                  key={sectionId}
                  href={`#${sectionId}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors hover:bg-slate-100"
                  style={{ color: colors.text }}
                >
                  <Icon size={16} style={{ color: colors.primary }} />
                  {config.name}
                </a>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Sections */}
      <main>
        {sectionsWithContent.map(sectionId => {
          const SectionComponent = SECTION_COMPONENTS[sectionId];
          if (!SectionComponent) return null;
          return <SectionComponent key={sectionId} data={data} />;
        })}
      </main>

      {/* Footer */}
      <footer 
        className="py-8 mt-12"
        style={{ backgroundColor: colors.primary }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-white/80 text-sm">
            {settings.footer_text || `© ${new Date().getFullYear()} ${organization.name}`}
          </p>
          {settings.social_links && (
            <div className="flex justify-center gap-4 mt-4">
              {settings.social_links.facebook && (
                <a 
                  href={settings.social_links.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                >
                  Facebook
                </a>
              )}
              {settings.social_links.instagram && (
                <a 
                  href={settings.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-white transition-colors"
                >
                  Instagram
                </a>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}
